import { createApi } from '@reduxjs/toolkit/query/react';
import { carBaseQuery } from '@app/api/carBaseQuery';
import type {
  MeetingGuidanceBuilderDraft,
  MeetingGuidanceBuilderDraftSaveRequest,
  MeetingGuidanceResponse,
  MeetingGuidanceSignalCatalogResponse,
  MeetingGuidanceTemplateListResponse,
  MeetingIntake,
  MeetingIntakeSaveRequest,
} from '../types';
import { API_BASE_PATH } from '../constants';

interface MeetingGuidanceRequest {
  planResultRootId: string;
  planResultTypeId: string;
}

export const meetingGuidanceApi = createApi({
  reducerPath: 'meetingGuidanceApi',
  baseQuery: carBaseQuery,
  tagTypes: ['MeetingGuidance', 'MeetingGuidanceBuilderDraft'],
  endpoints: (builder) => ({
    getMeetingGuidance: builder.query<
      MeetingGuidanceResponse,
      MeetingGuidanceRequest
    >({
      query: ({ planResultRootId, planResultTypeId }) => ({
        url: `${API_BASE_PATH}/${planResultRootId}/${planResultTypeId}/meeting-guidance/`,
        method: 'GET',
      }),
      providesTags: ['MeetingGuidance'],
    }),
    getMeetingGuidanceTemplates: builder.query<
      MeetingGuidanceTemplateListResponse,
      MeetingGuidanceRequest
    >({
      query: ({ planResultRootId, planResultTypeId }) => ({
        url: `${API_BASE_PATH}/${planResultRootId}/${planResultTypeId}/meeting-guidance/templates/`,
        method: 'GET',
      }),
    }),
    getMeetingGuidanceSignalCatalog: builder.query<
      MeetingGuidanceSignalCatalogResponse,
      MeetingGuidanceRequest
    >({
      query: ({ planResultRootId, planResultTypeId }) => ({
        url: `${API_BASE_PATH}/${planResultRootId}/${planResultTypeId}/meeting-guidance/signals/`,
        method: 'GET',
      }),
    }),
    saveMeetingGuidanceIntake: builder.mutation<
      MeetingIntake,
      MeetingGuidanceRequest & { intake: MeetingIntakeSaveRequest }
    >({
      query: ({ planResultRootId, planResultTypeId, intake }) => ({
        url: `${API_BASE_PATH}/${planResultRootId}/${planResultTypeId}/meeting-guidance/intake/`,
        method: 'PUT',
        body: intake,
      }),
      invalidatesTags: ['MeetingGuidance'],
    }),
    getMeetingGuidanceBuilderDraft: builder.query<
      MeetingGuidanceBuilderDraft,
      MeetingGuidanceRequest
    >({
      query: ({ planResultRootId, planResultTypeId }) => ({
        url: `${API_BASE_PATH}/${planResultRootId}/${planResultTypeId}/meeting-guidance/builder-draft/`,
        method: 'GET',
      }),
      providesTags: ['MeetingGuidanceBuilderDraft'],
    }),
    saveMeetingGuidanceBuilderDraft: builder.mutation<
      MeetingGuidanceBuilderDraft,
      MeetingGuidanceRequest & { builderDraft: MeetingGuidanceBuilderDraftSaveRequest }
    >({
      query: ({ planResultRootId, planResultTypeId, builderDraft }) => ({
        url: `${API_BASE_PATH}/${planResultRootId}/${planResultTypeId}/meeting-guidance/builder-draft/`,
        method: 'PUT',
        body: builderDraft,
      }),
      invalidatesTags: ['MeetingGuidance', 'MeetingGuidanceBuilderDraft'],
    }),
  }),
});

export const {
  useGetMeetingGuidanceBuilderDraftQuery,
  useGetMeetingGuidanceQuery,
  useGetMeetingGuidanceSignalCatalogQuery,
  useGetMeetingGuidanceTemplatesQuery,
  useSaveMeetingGuidanceBuilderDraftMutation,
  useSaveMeetingGuidanceIntakeMutation,
} = meetingGuidanceApi;
